import { PlanningDay, PlanningDayValue, planningLevels, PlanningWeek } from "../../../../interfaces/planning";
import { t } from "i18next";
import { Form } from "react-bootstrap";
import { updatePlanningDay, updatePlanningDaySession, deletePlanningDaySession } from "../../../../services/planning";
import { useEffect, useState } from "react";
import { Training } from "../../../../interfaces/training";
import { TrainingAutocompleteInput } from "./TrainingAutocompleteInput";

interface CoachPlanningDayRowProps {
    team_id: number;
    season_id: number;
    week: PlanningWeek;
    day: PlanningDay;
    saveDay: CallableFunction;
    tabIndex: number;
    trainings: Training[];
    trainingsLoaded: boolean;
    onDayContextMenu?: (event: React.MouseEvent, dayKey: string) => void;
    onRefresh?: CallableFunction;
}

interface DayValue {
    field: string;
    value: string
}

interface DaySession {
    day_moment_session: string;
    fields: DayValue[];
}
interface DayMoment {
    day_moment: string;
    sessions: DaySession[];
};

function CoachPlanningDayRow(props: CoachPlanningDayRowProps) {
    const [loaded, setLoaded] = useState(false);
    const [day, setDay] = useState<PlanningDay>(props.day);
    const [totalSessions, setTotalSessions] = useState(2);
    const [totalSessionsMorning, setTotalSessionsMorning] = useState(1);
    const [totalSessionsAfternoon, setTotalSessionsAfternoon] = useState(1);
    const [morningSessions, setMorningSessions] = useState<DaySession[]>([]);
    const [afternoonSessions, setAfternoonSessions] = useState<DaySession[]>([]);
    const [newSessionId, setNewSessionId] = useState<string | null>(null);
    const [formKey, setFormKey] = useState<number>(0); // Para forzar re-render de inputs
    const [isSelectingTraining, setIsSelectingTraining] = useState<boolean>(false); // Para prevenir conflictos con handleCellChange

    const prepareMoment = (values: PlanningDayValue[]) => {
        return values.reduce<DayMoment[]>((acc, item) => {
            let dayMomentObj: DayMoment | undefined = acc.find(
            d => d.day_moment === item.day_moment
            )
            
            if (!dayMomentObj) {
            dayMomentObj = {
                day_moment: item.day_moment,
                sessions: []
            }
            acc.push(dayMomentObj)
            }
            
            let sessionObj: DaySession | undefined = dayMomentObj.sessions.find(
            s => s.day_moment_session === item.day_moment_session
            )
            
            if (!sessionObj) {
            sessionObj = {
                day_moment_session: item.day_moment_session,
                fields: []
            }
            dayMomentObj.sessions.push(sessionObj)
            }
            
            const dayValue: DayValue = {
            field: item.field,
            value: item.value ?? '' // Usar nullish coalescing
            }
            
            sessionObj.fields.push(dayValue)
            
            return acc
        }, []);
    }

    useEffect(() => {
        setDay(props.day);
        let moments: DayMoment[] = prepareMoment(props.day.values.filter((v: PlanningDayValue) => v.day_moment == 'morning'));
        const newMorningSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
        setMorningSessions(newMorningSessions);
        setTotalSessionsMorning(newMorningSessions.length);
        moments = prepareMoment(props.day.values.filter((v: PlanningDayValue) => v.day_moment == 'afternoon'));
        const newAfternoonSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
        setAfternoonSessions(newAfternoonSessions);
        setTotalSessionsAfternoon(newAfternoonSessions.length)
        setTotalSessions(newMorningSessions.length + newAfternoonSessions.length);

        setLoaded(true);
    }, [props.day]);
    
    const handleCellChange = (event: any) => {
        const body_obj = {
            season: event.currentTarget.dataset.season,
            team: event.currentTarget.dataset.team,
            week: event.currentTarget.dataset.week,
            day: event.currentTarget.dataset.day,
            moment: event.currentTarget.dataset.moment,
            session: event.currentTarget.dataset.session,
            field: event.currentTarget.dataset.field,
            value: event.currentTarget.value
        }
        
        updatePlanningDay(body_obj)
            .then(response => {
                if (response.data.status == 1) {
                    setDay(response.data.data);
                    props.saveDay(props.week.key, response.data.data);
                } else {
                    alert(t("Data could not be saved. Try again later."));
                }
            });
    }

    const getDefaultValue = (moment: string, session: string, field: string): string => {
        let output = '';

        let sessions: DaySession[] = [];

        if (moment == 'morning') {
            sessions = morningSessions;
        } else {
            sessions = afternoonSessions;
        }

        const sessionIndex = sessions.findIndex((s: DaySession) => s.day_moment_session == session);
        if (sessionIndex !== -1) {
            const fieldIndex = sessions[sessionIndex].fields.findIndex((f: DayValue) => f.field == field);
            if (fieldIndex !== -1) {
                output = sessions[sessionIndex].fields[fieldIndex].value;

                // Para campos numéricos de planningLevels, convertir coma decimal a punto
                // porque los inputs type="number" solo aceptan punto como separador decimal
                const isNumericField = planningLevels.some(level => level.key === field);
                if (isNumericField && output && output.includes(',')) {
                    output = output.replace(',', '.');
                }
            }
        }

        return output;
    }

    const handleAddSession = (event: any) => {
        event.preventDefault();

        // Capturar los valores del evento antes de la llamada asíncrona
        const moment = event.currentTarget.dataset.moment;
        const season = event.currentTarget.dataset.season;
        const team = event.currentTarget.dataset.team;
        const week = event.currentTarget.dataset.week;
        const day = event.currentTarget.dataset.day;

        let maxSession = 1;
        if (moment == 'morning') {
            maxSession = Math.max(...morningSessions.map(s => Number(s.day_moment_session)))
        } else {
            maxSession = Math.max(...afternoonSessions.map(s => Number(s.day_moment_session)))
        }

        const body_obj = {
            season: season,
            team: team,
            week: week,
            day: day,
            moment: moment,
            session: (maxSession + 1).toString(),
            field: 'type',
            value: ''
        }

        updatePlanningDay(body_obj)
            .then(response => {
                if (response.data.status == 1) {
                    const updatedDay = response.data.data;
                    setDay(updatedDay);
                    props.saveDay(props.week.key, updatedDay);

                    // Establecer el ID de la nueva sesión para la animación
                    setNewSessionId(moment + '_' + (maxSession + 1).toString());

                    // Recalcular las sesiones
                    if (moment == 'morning') {
                        let moments: DayMoment[] = prepareMoment(updatedDay.values.filter((v: PlanningDayValue) => v.day_moment == 'morning'));
                        const newMorningSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
                        setMorningSessions(newMorningSessions);
                        setTotalSessionsMorning(newMorningSessions.length);
                        setTotalSessions(newMorningSessions.length + afternoonSessions.length);
                    } else {
                        let moments: DayMoment[] = prepareMoment(updatedDay.values.filter((v: PlanningDayValue) => v.day_moment == 'afternoon'));
                        const newAfternoonSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
                        setAfternoonSessions(newAfternoonSessions);
                        setTotalSessionsAfternoon(newAfternoonSessions.length);
                        setTotalSessions(morningSessions.length + newAfternoonSessions.length);
                    }

                    // Limpiar el ID de la nueva sesión después de la animación
                    setTimeout(() => {
                        setNewSessionId(null);
                    }, 2000);
                } else {
                    alert(t("Data could not be saved. Try again later."));
                }
            });
    }

    const handleDeleteSession = (event: any) => {
        event.preventDefault();

        // Capturar los valores del evento antes de la llamada asíncrona
        const moment = event.currentTarget.dataset.moment;
        const season = event.currentTarget.dataset.season;
        const team = event.currentTarget.dataset.team;
        const week = event.currentTarget.dataset.week;
        const day = event.currentTarget.dataset.day;
        const session = event.currentTarget.dataset.session;

        // Determinar si es la última sesión del momento
        const currentSessions = moment === 'morning' ? morningSessions : afternoonSessions;
        const isLastSession = currentSessions.length === 1;

        if (isLastSession) {
            // Si es la última sesión, limpiar todos los campos en lugar de eliminar
            if (!window.confirm(t("Are you sure you want to clear all fields in this session?"))) {
                return;
            }

            const body_obj = {
                season: season,
                team: team,
                week: week,
                day: day,
                moment: moment,
                session: session,
                type: '',
                pct: '',
                description: '',
                values: planningLevels.map(level => ({ field: level.key, value: '' }))
                    .concat([
                        { field: 'KM', value: '' },
                        { field: 'TH', value: '' }
                    ])
            }

            updatePlanningDaySession(body_obj)
                .then(response => {
                    if (response.data.status == 1) {
                        const updatedDay = response.data.data;

                        // Validar que updatedDay tenga la estructura esperada
                        if (!updatedDay || !Array.isArray(updatedDay.values)) {
                            // Fallback: refresh completo si la respuesta no tiene la estructura esperada
                            if (props.onRefresh) {
                                props.onRefresh();
                                return;
                            }
                            console.error('Invalid updatedDay structure:', updatedDay);
                            alert(t("Data could not be saved. Try again later."));
                            return;
                        }

                        setDay(updatedDay);
                        props.saveDay(props.week.key, updatedDay);

                        // Recalcular las sesiones
                        let moments: DayMoment[] = prepareMoment(updatedDay.values.filter((v: PlanningDayValue) => v.day_moment == moment));
                        if (moment === 'morning') {
                            const newMorningSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
                            setMorningSessions(newMorningSessions);
                        } else {
                            const newAfternoonSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
                            setAfternoonSessions(newAfternoonSessions);
                        }

                        // Forzar re-render de los inputs
                        setFormKey(prevKey => prevKey + 1);
                    } else {
                        alert(t("Data could not be saved. Try again later."));
                    }
                });
        } else {
            // Si hay múltiples sesiones, eliminar normalmente
            if (!window.confirm(t("Are you sure you want to delete this session? This action cannot be undone."))) {
                return;
            }

            // Añadir clase de animación a la fila
            const row = event.currentTarget.closest('tr');
            if (row) {
                row.classList.add('deleting');
            }

            const body_obj = {
                season: season,
                team: team,
                week: week,
                day: day,
                moment: moment,
                session: session
            }

            // Esperar un poco para que se vea la animación antes de eliminar
            setTimeout(() => {
                deletePlanningDaySession(body_obj)
                    .then(response => {
                        if (response.data.status == 1) {
                            const updatedDay = response.data.data;

                            // Validar que updatedDay tenga la estructura esperada
                            if (!updatedDay || !Array.isArray(updatedDay.values)) {
                                // Fallback: refresh completo si la respuesta no tiene la estructura esperada
                                if (props.onRefresh) {
                                    props.onRefresh();
                                    return;
                                }
                                console.error('Invalid updatedDay structure:', updatedDay);
                                alert(t("Data could not be saved. Try again later."));
                                if (row) {
                                    row.classList.remove('deleting');
                                }
                                return;
                            }

                            setDay(updatedDay);
                            props.saveDay(props.week.key, updatedDay);

                            // Recalcular las sesiones
                            if (moment == 'morning') {
                                let moments: DayMoment[] = prepareMoment(updatedDay.values.filter((v: PlanningDayValue) => v.day_moment == 'morning'));
                                const newMorningSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
                                setMorningSessions(newMorningSessions);
                                setTotalSessionsMorning(newMorningSessions.length);
                                setTotalSessions(newMorningSessions.length + afternoonSessions.length);
                            } else {
                                let moments: DayMoment[] = prepareMoment(updatedDay.values.filter((v: PlanningDayValue) => v.day_moment == 'afternoon'));
                                const newAfternoonSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
                                setAfternoonSessions(newAfternoonSessions);
                                setTotalSessionsAfternoon(newAfternoonSessions.length);
                                setTotalSessions(morningSessions.length + newAfternoonSessions.length);
                            }
                        } else {
                            alert(t("Data could not be saved. Try again later."));
                            // Quitar la clase si falla
                            if (row) {
                                row.classList.remove('deleting');
                            }
                        }
                    });
            }, 100);
        }
    }


    const handleSelectTraining = async (training: Training, moment: string, session: string, shouldFillAllFields: boolean = true) => {
        // Activar flag para prevenir que handleCellChange interfiera
        setIsSelectingTraining(true);

        // Helper function to get value for a key from training values
        // Busca de forma case-insensitive
        const getTrainingValue = (key: string): string => {
            if (!training.values || training.values.length === 0) {
                return '';
            }
            // Buscar de forma case-insensitive
            const value = training.values.find(v =>
                v.value_key?.toUpperCase() === key.toUpperCase()
            );
            return value ? value.value : '';
        };

        // Preparar el array de values con todos los planning levels
        const values: Array<{field: string, value: string}> = [];

        // Solo agregar valores si shouldFillAllFields es true
        if (shouldFillAllFields) {
            // Agregar planning levels
            planningLevels.forEach(level => {
                const value = getTrainingValue(level.key);
                values.push({ field: level.key, value: value });
            });

            // Agregar KM y TH
            values.push({ field: 'KM', value: getTrainingValue('KM') });
            values.push({ field: 'TH', value: getTrainingValue('TH') });
        }

        // Construir el body según el formato esperado por el endpoint
        const body_obj: any = {
            season: props.season_id.toString(),
            team: props.team_id.toString(),
            week: props.week.key,
            day: props.day.key,
            moment: moment,
            session: session,
            type: training.tipo || '',
            pct: training.carga ? training.carga.toString() : '',
            description: training.descripcion || ''
        };

        // Solo incluir values si shouldFillAllFields es true
        if (shouldFillAllFields) {
            body_obj.values = values;
        }

        try {
            const response = await updatePlanningDaySession(body_obj);

            if (response.data.status == 1) {
                const updatedDay = response.data.data;
                setDay(updatedDay);
                props.saveDay(props.week.key, updatedDay);

                // Recargar las sesiones para refrescar todos los inputs
                let moments: DayMoment[] = prepareMoment(updatedDay.values.filter((v: PlanningDayValue) => v.day_moment == moment));
                if (moment === 'morning') {
                    const newMorningSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
                    setMorningSessions(newMorningSessions);
                } else {
                    const newAfternoonSessions: DaySession[] = moments.length == 0 ? [{ day_moment_session: '1', fields: [] }] : moments[0].sessions;
                    setAfternoonSessions(newAfternoonSessions);
                }

                // Forzar re-render de todos los inputs incrementando la key
                setFormKey(prevKey => prevKey + 1);
            } else {
                console.error('Status no es 1:', response.data);
                console.error('Mensaje de error:', response.data.message);
                alert(t("Data could not be saved. Try again later."));
            }
        } catch (error) {
            console.error('Error updating session:', error);
            console.error('Error completo:', error);
            alert(t("Data could not be saved. Try again later."));
        } finally {
            // Desactivar flag después de completar la operación
            setTimeout(() => {
                setIsSelectingTraining(false);
            }, 300);
        }
    };


    return (
        <>
            {/* Show day rows only when both day data and trainings are loaded */}
            { loaded && props.trainingsLoaded &&
                <>
                    <tr key={`morning_row_${morningSessions[0]?.day_moment_session}_${formKey}`} className={`coach-planning-day-row ${newSessionId === 'morning_' + morningSessions[0]?.day_moment_session ? 'new-session' : ''}`}>
                        <td rowSpan={ totalSessions } className="day sticky"
                            onContextMenu={(e) => props.onDayContextMenu && props.onDayContextMenu(e, props.day.key)}>
                            <div className="day-weekday">{ new Date(props.day.key).toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '').charAt(0).toUpperCase() + new Date(props.day.key).toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '').slice(1) }</div>
                            <div className="day-number">{ props.day.name }</div>
                        </td>
                        <td rowSpan={ totalSessionsMorning } className="moment sticky">
                            <div>{ t("morning") }<button data-season={ props.season_id } data-team={ props.team_id }
                                            data-week={ props.week.key } data-day={ props.day.key }
                                            data-moment={ 'morning' } title={ t("Add a new session") }
                                            className="add-session" onClick={ handleAddSession }><i className="bi bi-plus-square"></i></button></div>
                        </td>
                        {
                            morningSessions.slice(0, 1).map((s: DaySession) => (
                                <>
                                    <td className="type">
                                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', position: 'relative' }}>
                                            <div style={{ flex: 1 }}>
                                                <TrainingAutocompleteInput
                                                    fieldType="type"
                                                    name={props.day.key + '_morning_' + s.day_moment_session + '_type'}
                                                    defaultValue={getDefaultValue('morning', s.day_moment_session, 'type')}
                                                    dataAttributes={{
                                                        season: props.season_id,
                                                        team: props.team_id,
                                                        week: props.week.key,
                                                        day: props.day.key,
                                                        moment: 'morning',
                                                        session: s.day_moment_session,
                                                        field: 'type'
                                                    }}
                                                    trainings={props.trainings}
                                                    onBlur={handleCellChange}
                                                    onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'morning', s.day_moment_session, shouldFill)}
                                                    ariaLabel={t("Type")}
                                                    isSelectingTraining={isSelectingTraining}
                                                />
                                            </div>
                                            <button data-season={ props.season_id } data-team={ props.team_id }
                                                data-week={ props.week.key } data-day={ props.day.key }
                                                data-moment={ 'morning' } data-session={ s.day_moment_session }
                                                title={ morningSessions.length > 1 ? t("Delete this session") : t("Clear all fields") }
                                                className="delete-session" onClick={ handleDeleteSession }>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                    <td className="type">
                                        <TrainingAutocompleteInput
                                            fieldType="pct"
                                            name={props.day.key + '_morning_' + s.day_moment_session + '_pct'}
                                            defaultValue={getDefaultValue('morning', s.day_moment_session, 'pct')}
                                            currentTipo={getDefaultValue('morning', s.day_moment_session, 'type')}
                                            dataAttributes={{
                                                season: props.season_id,
                                                team: props.team_id,
                                                week: props.week.key,
                                                day: props.day.key,
                                                moment: 'morning',
                                                session: s.day_moment_session,
                                                field: 'pct'
                                            }}
                                            trainings={props.trainings}
                                            onBlur={handleCellChange}
                                            onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'morning', s.day_moment_session, shouldFill)}
                                            ariaLabel={t("%")}
                                            isSelectingTraining={isSelectingTraining}
                                        />
                                    </td>
                                    <td className="decription">
                                        <TrainingAutocompleteInput
                                            fieldType="description"
                                            name={props.day.key + '_morning_' + s.day_moment_session + '_description'}
                                            defaultValue={getDefaultValue('morning', s.day_moment_session, 'description')}
                                            placeholder="-"
                                            currentTipo={getDefaultValue('morning', s.day_moment_session, 'type')}
                                            currentPct={getDefaultValue('morning', s.day_moment_session, 'pct')}
                                            dataAttributes={{
                                                season: props.season_id,
                                                team: props.team_id,
                                                week: props.week.key,
                                                day: props.day.key,
                                                moment: 'morning',
                                                session: s.day_moment_session,
                                                field: 'description'
                                            }}
                                            trainings={props.trainings}
                                            onBlur={handleCellChange}
                                            onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'morning', s.day_moment_session, shouldFill)}
                                            ariaLabel={t("Description")}
                                            isSelectingTraining={isSelectingTraining}
                                        />
                                    </td>
                                    {
                                        planningLevels.map((l: any, index) => (
                                            <td className="level" key={`morning_${s.day_moment_session}_${l.key}_${formKey}`}>
                                                <Form.Control type="number" step="0.1" aria-label={ l.name }
                                                    key={`input_morning_${s.day_moment_session}_${l.key}_${formKey}`}
                                                    name={ props.day.key + '_morning_' + s.day_moment_session + '_' + l.key }
                                                    data-season={ props.season_id } data-team={ props.team_id }
                                                    data-week={ props.week.key } data-day={ props.day.key }
                                                    data-moment={ 'morning' } data-session={ s.day_moment_session }
                                                    data-field={ l.key } placeholder="-"
                                                    defaultValue={ getDefaultValue('morning', s.day_moment_session, l.key) }
                                                    onBlur={ handleCellChange } />
                                            </td>
                                        ))
                                    }
                                    <td className="level">
                                        <Form.Control type="text" aria-label={ 'KM' }
                                            key={`input_morning_${s.day_moment_session}_KM_${formKey}`}
                                            name={ props.day.key + '_morning_' + s.day_moment_session + '_KM' }
                                            data-season={ props.season_id } data-team={ props.team_id }
                                            data-week={ props.week.key } data-day={ props.day.key }
                                            data-moment={ 'morning' } data-session={ s.day_moment_session }
                                            data-field={ 'KM' } placeholder="-"
                                            defaultValue={ getDefaultValue('morning', s.day_moment_session, 'KM') }
                                            onBlur={ handleCellChange } />
                                    </td>
                                    <td className="level">
                                        <Form.Control type="text" aria-label={ 'TH' }
                                            key={`input_morning_${s.day_moment_session}_TH_${formKey}`}
                                            name={ props.day.key + '_morning_' + s.day_moment_session + '_TH' }
                                            data-season={ props.season_id } data-team={ props.team_id }
                                            data-week={ props.week.key } data-day={ props.day.key }
                                            data-moment={ 'morning' } data-session={ s.day_moment_session }
                                            data-field={ 'TH' } placeholder="-"
                                            defaultValue={ getDefaultValue('morning', s.day_moment_session, 'TH') }
                                            onBlur={ handleCellChange } />
                                    </td>
                                </>
                            ))
                        }
                    </tr>
                    { morningSessions.length > 1 &&
                        morningSessions.slice(1).map((s: DaySession) => (
                            <tr key={`morning_row_${s.day_moment_session}_${formKey}`} className={`coach-planning-day-row ${newSessionId === 'morning_' + s.day_moment_session ? 'new-session' : ''}`}>
                                <td className="type">
                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', position: 'relative' }}>
                                        <div style={{ flex: 1 }}>
                                            <TrainingAutocompleteInput
                                                fieldType="type"
                                                name={props.day.key + '_morning_' + s.day_moment_session + '_type'}
                                                defaultValue={getDefaultValue('morning', s.day_moment_session, 'type')}
                                                dataAttributes={{
                                                    season: props.season_id,
                                                    team: props.team_id,
                                                    week: props.week.key,
                                                    day: props.day.key,
                                                    moment: 'morning',
                                                    session: s.day_moment_session,
                                                    field: 'type'
                                                }}
                                                trainings={props.trainings}
                                                onBlur={handleCellChange}
                                                onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'morning', s.day_moment_session, shouldFill)}
                                                ariaLabel={t("Type")}
                                                isSelectingTraining={isSelectingTraining}
                                            />
                                        </div>
                                        <button data-season={ props.season_id } data-team={ props.team_id }
                                            data-week={ props.week.key } data-day={ props.day.key }
                                            data-moment={ 'morning' } data-session={ s.day_moment_session }
                                            title={ t("Delete this session") }
                                            className="delete-session" onClick={ handleDeleteSession }>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </td>
                                <td className="type">
                                    <TrainingAutocompleteInput
                                        fieldType="pct"
                                        name={props.day.key + '_morning_' + s.day_moment_session + '_pct'}
                                        defaultValue={getDefaultValue('morning', s.day_moment_session, 'pct')}
                                        currentTipo={getDefaultValue('morning', s.day_moment_session, 'type')}
                                        dataAttributes={{
                                            season: props.season_id,
                                            team: props.team_id,
                                            week: props.week.key,
                                            day: props.day.key,
                                            moment: 'morning',
                                            session: s.day_moment_session,
                                            field: 'pct'
                                        }}
                                        trainings={props.trainings}
                                        onBlur={handleCellChange}
                                        onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'morning', s.day_moment_session, shouldFill)}
                                        ariaLabel={t("%")}
                                        isSelectingTraining={isSelectingTraining}
                                    />
                                </td>
                                <td className="decription">
                                    <TrainingAutocompleteInput
                                        fieldType="description"
                                        name={props.day.key + '_morning_' + s.day_moment_session + '_description'}
                                        defaultValue={getDefaultValue('morning', s.day_moment_session, 'description')}
                                        placeholder="-"
                                        currentTipo={getDefaultValue('morning', s.day_moment_session, 'type')}
                                        currentPct={getDefaultValue('morning', s.day_moment_session, 'pct')}
                                        dataAttributes={{
                                            season: props.season_id,
                                            team: props.team_id,
                                            week: props.week.key,
                                            day: props.day.key,
                                            moment: 'morning',
                                            session: s.day_moment_session,
                                            field: 'description'
                                        }}
                                        trainings={props.trainings}
                                        onBlur={handleCellChange}
                                        onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'morning', s.day_moment_session, shouldFill)}
                                        ariaLabel={t("Description")}
                                        isSelectingTraining={isSelectingTraining}
                                    />
                                </td>
                                {
                                    planningLevels.map((l: any, index) => (
                                        <td className="level" key={`morning_extra_${s.day_moment_session}_${l.key}_${formKey}`}>
                                            <Form.Control type="number" step="0.1" aria-label={ l.name }
                                                key={`input_morning_extra_${s.day_moment_session}_${l.key}_${formKey}`}
                                                name={ props.day.key + '_morning_' + s.day_moment_session + '_' + l.key }
                                                data-season={ props.season_id } data-team={ props.team_id }
                                                data-week={ props.week.key } data-day={ props.day.key }
                                                data-moment={ 'morning' } data-session={ s.day_moment_session }
                                                data-field={ l.key } placeholder="-"
                                                defaultValue={ getDefaultValue('morning', s.day_moment_session, l.key) }
                                                onBlur={ handleCellChange } />
                                        </td>
                                    ))
                                }
                                <td className="level">
                                    <Form.Control type="text" aria-label={ 'KM' }
                                        key={`input_morning_extra_${s.day_moment_session}_KM_${formKey}`}
                                        name={ props.day.key + '_morning_' + s.day_moment_session + '_KM' }
                                        data-season={ props.season_id } data-team={ props.team_id }
                                        data-week={ props.week.key } data-day={ props.day.key }
                                        data-moment={ 'morning' } data-session={ s.day_moment_session }
                                        data-field={ 'KM' } placeholder="-"
                                        defaultValue={ getDefaultValue('morning', s.day_moment_session, 'KM') }
                                        onBlur={ handleCellChange } />
                                </td>
                                <td className="level">
                                    <Form.Control type="text" aria-label={ 'TH' }
                                        key={`input_morning_extra_${s.day_moment_session}_TH_${formKey}`}
                                        name={ props.day.key + '_morning_' + s.day_moment_session + '_TH' }
                                        data-season={ props.season_id } data-team={ props.team_id }
                                        data-week={ props.week.key } data-day={ props.day.key }
                                        data-moment={ 'morning' } data-session={ s.day_moment_session }
                                        data-field={ 'TH' } placeholder="-"
                                        defaultValue={ getDefaultValue('morning', s.day_moment_session, 'TH') }
                                        onBlur={ handleCellChange } />
                                </td>
                            </tr>
                            ))
                    }
                    <tr key={`afternoon_row_${afternoonSessions[0]?.day_moment_session}_${formKey}`} className={`coach-planning-day-row ${newSessionId === 'afternoon_' + afternoonSessions[0]?.day_moment_session ? 'new-session' : ''}`}>
                        <td rowSpan={ totalSessionsAfternoon } className="moment sticky">
                            <div>{ t("afternoon") }<button data-season={ props.season_id } data-team={ props.team_id }
                                            data-week={ props.week.key } data-day={ props.day.key }
                                            data-moment={ 'afternoon' } title={ t("Add a new session") }
                                            className="add-session" onClick={ handleAddSession }><i className="bi bi-plus-square"></i></button></div>
                        </td>
                        {
                            afternoonSessions.slice(0, 1).map((s: DaySession) => (
                                <>
                                    <td className="type">
                                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', position: 'relative' }}>
                                            <div style={{ flex: 1 }}>
                                                <TrainingAutocompleteInput
                                                    fieldType="type"
                                                    name={props.day.key + '_afternoon_' + s.day_moment_session + '_type'}
                                                    defaultValue={getDefaultValue('afternoon', s.day_moment_session, 'type')}
                                                    dataAttributes={{
                                                        season: props.season_id,
                                                        team: props.team_id,
                                                        week: props.week.key,
                                                        day: props.day.key,
                                                        moment: 'afternoon',
                                                        session: s.day_moment_session,
                                                        field: 'type'
                                                    }}
                                                    trainings={props.trainings}
                                                    onBlur={handleCellChange}
                                                    onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'afternoon', s.day_moment_session, shouldFill)}
                                                    ariaLabel={t("Type")}
                                                    isSelectingTraining={isSelectingTraining}
                                                />
                                            </div>
                                            <button data-season={ props.season_id } data-team={ props.team_id }
                                                data-week={ props.week.key } data-day={ props.day.key }
                                                data-moment={ 'afternoon' } data-session={ s.day_moment_session }
                                                title={ afternoonSessions.length > 1 ? t("Delete this session") : t("Clear all fields") }
                                                className="delete-session" onClick={ handleDeleteSession }>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                    <td className="type">
                                        <TrainingAutocompleteInput
                                            fieldType="pct"
                                            name={props.day.key + '_afternoon_' + s.day_moment_session + '_pct'}
                                            defaultValue={getDefaultValue('afternoon', s.day_moment_session, 'pct')}
                                            currentTipo={getDefaultValue('afternoon', s.day_moment_session, 'type')}
                                            dataAttributes={{
                                                season: props.season_id,
                                                team: props.team_id,
                                                week: props.week.key,
                                                day: props.day.key,
                                                moment: 'afternoon',
                                                session: s.day_moment_session,
                                                field: 'pct'
                                            }}
                                            trainings={props.trainings}
                                            onBlur={handleCellChange}
                                            onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'afternoon', s.day_moment_session, shouldFill)}
                                            ariaLabel={t("%")}
                                            isSelectingTraining={isSelectingTraining}
                                        />
                                    </td>
                                    <td className="decription">
                                        <TrainingAutocompleteInput
                                            fieldType="description"
                                            name={props.day.key + '_afternoon_' + s.day_moment_session + '_description'}
                                            defaultValue={getDefaultValue('afternoon', s.day_moment_session, 'description')}
                                            placeholder="-"
                                            currentTipo={getDefaultValue('afternoon', s.day_moment_session, 'type')}
                                            currentPct={getDefaultValue('afternoon', s.day_moment_session, 'pct')}
                                            dataAttributes={{
                                                season: props.season_id,
                                                team: props.team_id,
                                                week: props.week.key,
                                                day: props.day.key,
                                                moment: 'afternoon',
                                                session: s.day_moment_session,
                                                field: 'description'
                                            }}
                                            trainings={props.trainings}
                                            onBlur={handleCellChange}
                                            onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'afternoon', s.day_moment_session, shouldFill)}
                                            ariaLabel={t("Description")}
                                            isSelectingTraining={isSelectingTraining}
                                        />
                                    </td>
                                    {
                                        planningLevels.map((l: any, index) => (
                                            <td className="level" key={`afternoon_${s.day_moment_session}_${l.key}_${formKey}`}>
                                                <Form.Control type="number" step="0.1" aria-label={ l.name }
                                                    key={`input_afternoon_${s.day_moment_session}_${l.key}_${formKey}`}
                                                    name={ props.day.key + '_afternoon_' + s.day_moment_session + '_' + l.key }
                                                    data-season={ props.season_id } data-team={ props.team_id }
                                                    data-week={ props.week.key } data-day={ props.day.key }
                                                    data-moment={ 'afternoon' } data-session={ s.day_moment_session }
                                                    data-field={ l.key } placeholder="-"
                                                    defaultValue={ getDefaultValue('afternoon', s.day_moment_session, l.key) }
                                                    onBlur={ handleCellChange } />
                                            </td>
                                        ))
                                    }
                                    <td className="level">
                                        <Form.Control type="text" aria-label={ 'KM' }
                                            key={`input_afternoon_${s.day_moment_session}_KM_${formKey}`}
                                            name={ props.day.key + '_afternoon_' + s.day_moment_session + '_KM' }
                                            data-season={ props.season_id } data-team={ props.team_id }
                                            data-week={ props.week.key } data-day={ props.day.key }
                                            data-moment={ 'afternoon' } data-session={ s.day_moment_session }
                                            data-field={ 'KM' } placeholder="-"
                                            defaultValue={ getDefaultValue('afternoon', s.day_moment_session, 'KM') }
                                            onBlur={ handleCellChange } />
                                    </td>
                                    <td className="level">
                                        <Form.Control type="text" aria-label={ 'TH' }
                                            key={`input_afternoon_${s.day_moment_session}_TH_${formKey}`}
                                            name={ props.day.key + '_afternoon_' + s.day_moment_session + '_TH' }
                                            data-season={ props.season_id } data-team={ props.team_id }
                                            data-week={ props.week.key } data-day={ props.day.key }
                                            data-moment={ 'afternoon' } data-session={ s.day_moment_session }
                                            data-field={ 'TH' } placeholder="-"
                                            defaultValue={ getDefaultValue('afternoon', s.day_moment_session, 'TH') }
                                            onBlur={ handleCellChange } />
                                    </td>
                                </>
                            ))
                        }
                    </tr>
                    {
                        afternoonSessions.slice(1).map((s: DaySession) => (
                            <tr key={`afternoon_row_${s.day_moment_session}_${formKey}`} className={`coach-planning-day-row ${newSessionId === 'afternoon_' + s.day_moment_session ? 'new-session' : ''}`}>
                                <td className="type">
                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', position: 'relative' }}>
                                        <div style={{ flex: 1 }}>
                                            <TrainingAutocompleteInput
                                                fieldType="type"
                                                name={props.day.key + '_afternoon_' + s.day_moment_session + '_type'}
                                                defaultValue={getDefaultValue('afternoon', s.day_moment_session, 'type')}
                                                dataAttributes={{
                                                    season: props.season_id,
                                                    team: props.team_id,
                                                    week: props.week.key,
                                                    day: props.day.key,
                                                    moment: 'afternoon',
                                                    session: s.day_moment_session,
                                                    field: 'type'
                                                }}
                                                trainings={props.trainings}
                                                onBlur={handleCellChange}
                                                onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'afternoon', s.day_moment_session, shouldFill)}
                                                ariaLabel={t("Type")}
                                                isSelectingTraining={isSelectingTraining}
                                            />
                                        </div>
                                        <button data-season={ props.season_id } data-team={ props.team_id }
                                            data-week={ props.week.key } data-day={ props.day.key }
                                            data-moment={ 'afternoon' } data-session={ s.day_moment_session }
                                            title={ t("Delete this session") }
                                            className="delete-session" onClick={ handleDeleteSession }>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </td>
                                <td className="type">
                                    <TrainingAutocompleteInput
                                        fieldType="pct"
                                        name={props.day.key + '_afternoon_' + s.day_moment_session + '_pct'}
                                        defaultValue={getDefaultValue('afternoon', s.day_moment_session, 'pct')}
                                        currentTipo={getDefaultValue('afternoon', s.day_moment_session, 'type')}
                                        dataAttributes={{
                                            season: props.season_id,
                                            team: props.team_id,
                                            week: props.week.key,
                                            day: props.day.key,
                                            moment: 'afternoon',
                                            session: s.day_moment_session,
                                            field: 'pct'
                                        }}
                                        trainings={props.trainings}
                                        onBlur={handleCellChange}
                                        onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'afternoon', s.day_moment_session, shouldFill)}
                                        ariaLabel={t("%")}
                                        isSelectingTraining={isSelectingTraining}
                                    />
                                </td>
                                <td className="decription">
                                    <TrainingAutocompleteInput
                                        fieldType="description"
                                        name={props.day.key + '_afternoon_' + s.day_moment_session + '_description'}
                                        defaultValue={getDefaultValue('afternoon', s.day_moment_session, 'description')}
                                        placeholder="-"
                                        currentTipo={getDefaultValue('afternoon', s.day_moment_session, 'type')}
                                        currentPct={getDefaultValue('afternoon', s.day_moment_session, 'pct')}
                                        dataAttributes={{
                                            season: props.season_id,
                                            team: props.team_id,
                                            week: props.week.key,
                                            day: props.day.key,
                                            moment: 'afternoon',
                                            session: s.day_moment_session,
                                            field: 'description'
                                        }}
                                        trainings={props.trainings}
                                        onBlur={handleCellChange}
                                        onSelectTraining={(training, shouldFill) => handleSelectTraining(training, 'afternoon', s.day_moment_session, shouldFill)}
                                        ariaLabel={t("Description")}
                                        isSelectingTraining={isSelectingTraining}
                                    />
                                </td>
                                {
                                    planningLevels.map((l: any, index) => (
                                        <td className="level" key={`afternoon_extra_${s.day_moment_session}_${l.key}_${formKey}`}>
                                            <Form.Control type="number" step="0.1" aria-label={ l.name }
                                                key={`input_afternoon_extra_${s.day_moment_session}_${l.key}_${formKey}`}
                                                name={ props.day.key + '_afternoon_' + s.day_moment_session + '_' + l.key }
                                                data-season={ props.season_id } data-team={ props.team_id }
                                                data-week={ props.week.key } data-day={ props.day.key }
                                                data-moment={ 'afternoon' } data-session={ s.day_moment_session }
                                                data-field={ l.key } placeholder="-"
                                                defaultValue={ getDefaultValue('afternoon', s.day_moment_session, l.key) }
                                                onBlur={ handleCellChange } />
                                        </td>
                                    ))
                                }
                                <td className="level">
                                    <Form.Control type="text" aria-label={ 'KM' }
                                        key={`input_afternoon_extra_${s.day_moment_session}_KM_${formKey}`}
                                        name={ props.day.key + '_afternoon_' + s.day_moment_session + '_KM' }
                                        data-season={ props.season_id } data-team={ props.team_id }
                                        data-week={ props.week.key } data-day={ props.day.key }
                                        data-moment={ 'afternoon' } data-session={ s.day_moment_session }
                                        data-field={ 'KM' } placeholder="-"
                                        defaultValue={ getDefaultValue('afternoon', s.day_moment_session, 'KM') }
                                        onBlur={ handleCellChange } />
                                </td>
                                <td className="level">
                                    <Form.Control type="text" aria-label={ 'TH' }
                                        key={`input_afternoon_extra_${s.day_moment_session}_TH_${formKey}`}
                                        name={ props.day.key + '_afternoon_' + s.day_moment_session + '_TH' }
                                        data-season={ props.season_id } data-team={ props.team_id }
                                        data-week={ props.week.key } data-day={ props.day.key }
                                        data-moment={ 'afternoon' } data-session={ s.day_moment_session }
                                        data-field={ 'TH' } placeholder="-"
                                        defaultValue={ getDefaultValue('afternoon', s.day_moment_session, 'TH') }
                                        onBlur={ handleCellChange } />
                                </td>
                            </tr>
                        ))
                    }
                </>
            }

        </>
    );
}

export default CoachPlanningDayRow;